<!-- Crizac Assist. Read-only: every figure names the endpoint it came from, and
     a change request is described, never applied. -->

<!-- The floating launcher stays - Junaid: "this floating is fine; on click, add
     a slider." It hides while the panel is open so it cannot sit over it. -->
<button
  type="button"
  class="ca-launcher"
  *ngIf="!open()"
  (click)="assistContext.openPanel()"
  aria-label="Open Crizac Assist">
  <mat-icon>auto_awesome</mat-icon>
</button>

<!-- Scrim: dims the console behind, so it reads as a panel over the page -->
<div class="ca-scrim" *ngIf="open()" (click)="close()" aria-hidden="true"></div>

<!-- Half-screen slider -->
<aside class="ca-panel" *ngIf="open()" role="dialog" aria-label="Crizac Assist">

  <header class="ca-head">
    <div class="ca-id">
      <span class="ca-mark" aria-hidden="true"></span>
      <div class="ca-title">
        <span class="ca-name">Crizac Assist</span>
        <span class="ca-scope" *ngIf="scopeLabel(); else globalScope">
          {{ scopeLabel() }}
          <button type="button" class="ca-scope-clear" (click)="clearScope()"
                  aria-label="Clear scope">&times;</button>
        </span>
        <ng-template #globalScope>
          <span class="ca-scope is-global">All universities</span>
        </ng-template>
      </div>
    </div>
    <div class="ca-head-actions">
      <button type="button" class="ca-icon" (click)="showIdeas = !showIdeas"
              [class.is-on]="showIdeas" title="Suggestions" aria-label="Toggle suggestions">
        <mat-icon>lightbulb</mat-icon>
      </button>
      <button type="button" class="ca-icon" (click)="clearConversation()"
              title="New conversation" aria-label="New conversation">
        <mat-icon>add_comment</mat-icon>
      </button>
      <button type="button" class="ca-icon" (click)="close()" title="Close" aria-label="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </header>

  <!-- Quick actions: the handful of things done all day -->
  <div class="ca-quick">
    <button type="button" class="ca-quick-btn" *ngFor="let a of quickActions"
            [disabled]="busy" (click)="quick(a)">
      {{ a.label }}
    </button>
  </div>

  <div class="ca-body">

    <!-- conversation -->
    <div class="ca-thread">
      <div class="ca-turn" *ngFor="let t of turns" [class.is-user]="t.from === 'user'">
        <div class="ca-bubble"
             [class.is-refused]="t.refused"
             [class.is-failed]="t.failed">
          {{ t.text }}

          <div class="ca-sources" *ngIf="t.sources?.length">
            <span *ngFor="let s of t.sources">{{ s.endpoint }}</span>
          </div>
          <div class="ca-degraded" *ngIf="t.degraded">
            Matched on keywords — the language model was unavailable.
          </div>
        </div>
      </div>

      <div class="ca-turn" *ngIf="busy">
        <div class="ca-bubble is-busy">
          <span class="ca-dot"></span> Reading the same data as the screen…
        </div>
      </div>
    </div>

    <!-- Suggestions on the right, as asked -->
    <aside class="ca-ideas" *ngIf="showIdeas" aria-label="Suggestions">
      <div class="ca-ideas-top">
        <b>Suggestions</b>
        <span>What you could ask next</span>
      </div>
      <div class="ca-ideas-body">
        <section class="ca-ideas-group" *ngFor="let g of suggestionGroups">
          <h5>{{ g.title }}</h5>
          <button type="button" class="ca-idea" *ngFor="let s of g.items"
                  [disabled]="busy" (click)="send(s.prompt)">
            <span>{{ s.label }}</span>
            <span class="ca-idea-go" aria-hidden="true">&rarr;</span>
          </button>
        </section>
      </div>
    </aside>
  </div>

  <!-- The approval card. Sits between the conversation and the composer,
       where the action line is, because it IS the action - what is about to
       happen and who is about to put their name to it. -->
  <div class="ca-approve" *ngIf="pendingPlan?.ok">
    <div class="ca-approve-head">
      <span class="ca-approve-tag" [class.is-close]="pendingPlan?.action === 'close'">
        {{ pendingPlan?.action === 'close' ? 'Close' : 'Open' }}
      </span>
      <b>{{ pendingPlan?.summary }}</b>
    </div>

    <dl class="ca-approve-grid">
      <div><dt>University</dt><dd>{{ pendingPlan?.universityName }}</dd></div>
      <div><dt>Intake</dt><dd>{{ pendingPlan?.intakeName || '—' }}</dd></div>
      <div><dt>Countries</dt><dd>{{ pendingPlan?.countries?.join(', ') }}</dd></div>
      <div><dt>Courses</dt><dd>{{ pendingPlan?.affected }}<span *ngIf="pendingPlan?.courseLevelName">
        &nbsp;({{ pendingPlan?.courseLevelName }})</span></dd></div>
    </dl>

    <div class="ca-approve-sample" *ngIf="pendingPlan?.sample?.length">
      e.g. {{ pendingPlan?.sample?.join(' · ') }}<span *ngIf="(pendingPlan?.affected || 0) > (pendingPlan?.sample?.length || 0)">
      &nbsp;and {{ (pendingPlan?.affected || 0) - (pendingPlan?.sample?.length || 0) }} more</span>
    </div>

    <div class="ca-approve-warn" *ngFor="let w of pendingPlan?.warnings">{{ w }}</div>

    <div class="ca-approve-foot">
      <!-- The name comes from the session, so this says who it will be
           recorded against rather than asking them to type it. -->
      <span class="ca-approve-who">
        Approving as <b>{{ pendingPlan?.requestedBy || 'this user' }}</b>
      </span>
      <span class="ca-approve-actions">
        <button type="button" class="ca-approve-no" [disabled]="applying" (click)="cancelPlan()">Cancel</button>
        <button type="button" class="ca-approve-yes" [disabled]="applying" (click)="approvePlan()">
          {{ applying ? 'Applying…' : 'Approve &amp; apply' }}
        </button>
      </span>
    </div>
  </div>

  <!-- What was just done, with a way back. The backup id is shown because it
       is the thing someone would quote when asking for a revert later. -->
  <div class="ca-applied" *ngIf="lastApplied">
    <span>
      Applied by <b>{{ lastApplied.approvedBy || 'you' }}</b>
      <em *ngIf="lastApplied.backupId">&nbsp;· backup {{ lastApplied.backupId }}</em>
    </span>
    <button type="button" [disabled]="reverting" (click)="undoLast()">
      {{ reverting ? 'Putting back…' : 'Undo' }}
    </button>
  </div>

  <!-- The action, stated directly above the text box -->
  <div class="ca-action" *ngIf="pendingAction"
       [class.is-pending]="pendingAction.summary.startsWith('Change')">
    <span class="ca-action-label">{{ pendingAction.summary }}</span>
    <span class="ca-action-detail">{{ pendingAction.detail }}</span>
  </div>

  <form class="ca-compose" (ngSubmit)="send()">
    <input
      type="text"
      #composer
      [(ngModel)]="draft"
      name="draft"
      [disabled]="busy"
      placeholder="Ask about universities, courses, intakes or countries…"
      aria-label="Message Crizac Assist" />
    <button type="submit" class="ca-send" [disabled]="!draft.trim() || busy" aria-label="Send">
      <mat-icon>arrow_upward</mat-icon>
    </button>
  </form>

  <!-- History underneath, collapsed -->
  <div class="ca-history">
    <button type="button" class="ca-history-toggle" (click)="showHistory = !showHistory"
            [attr.aria-expanded]="showHistory">
      <mat-icon>{{ showHistory ? 'expand_more' : 'expand_less' }}</mat-icon>
      History
      <span class="ca-history-count" *ngIf="history.length">{{ history.length }}</span>
    </button>
    <div class="ca-history-list" *ngIf="showHistory">
      <p class="ca-history-empty" *ngIf="!history.length">
        Nothing yet. Questions you ask are listed here.
      </p>
      <div class="ca-history-row" *ngFor="let h of history">
        <button type="button" class="ca-history-q" (click)="send(h.question)" [disabled]="busy">
          {{ h.question }}
        </button>
        <span class="ca-history-meta">{{ h.scope }} · {{ h.at | date:'short' }}</span>
      </div>
      <button type="button" class="ca-history-clear" *ngIf="history.length"
              (click)="clearHistory()">Clear history</button>
    </div>
  </div>

  <footer class="ca-foot">
    Reads only. A change is drafted here and applied from the intake screen,
    which shows a summary before saving and keeps a revertible backup.
  </footer>
</aside>
